import MutationTypes from './mutationTypes'
import ActionTypes from './actionTypes'
import GetterTypes from './getterTypes'
import metaMutations from '../metaModule/mutationTypes'
import notificationMutations from '../notificationModule/mutationTypes'
import { logIn, logOut, register, fetchUser } from '@/api/v4/auth'
import { resetPassword, forgotPassword } from '@/api/v4/account'
import { fetchProfile, updateProfile } from '@/api/v4/profile'
import { getPaymentMethod } from '@/api/v4/billing'
import {
  PasswordResetDto,
  LogInDto,
  RegisterDto,
  Profile,
  PaymentMethod,
  UserType,
} from '@/types'
import { Store } from 'vuex'
import { COACH_PLANS } from '@/constants'
import Bugsnag from '@bugsnag/js'

export interface UserState {
  user: any
  fetchingUser: boolean
  forgotPasswordEmail: string | null
  profile: Profile | null
  paymentMethod: PaymentMethod | null
  excludedSkillCount: number
  actingAsClient: any | null
}

const rootState: UserState = {
  user: null,
  fetchingUser: false,
  forgotPasswordEmail: null,
  profile: null,
  paymentMethod: null,
  excludedSkillCount: -1,
  actingAsClient: null,
}

const mutations = {
  [MutationTypes.SET_USER.VALUE]: (state: UserState, user: any) => {
    state.user = user
  },
  [MutationTypes.SET_FETCHING_USER_STATUS.VALUE]: (
    state: UserState,
    isFetchingUser: any
  ) => {
    state.fetchingUser = isFetchingUser
  },
  [MutationTypes.SET_FORGOT_PASSWORD_EMAIL.VALUE]: (
    state: UserState,
    email: string
  ) => {
    state.forgotPasswordEmail = email
  },
  [MutationTypes.SET_USER_PROFILE.VALUE]: (state: UserState, profile: any) => {
    state.profile = profile
  },
  [MutationTypes.SET_PAYMENT_METHOD.VALUE]: (
    state: UserState,
    paymentMethod: PaymentMethod
  ) => {
    state.paymentMethod = paymentMethod
  },
  [MutationTypes.SET_EXCLUDED_SKILL_COUNT.VALUE]: (
    state: UserState,
    count: number
  ) => {
    state.excludedSkillCount = count
  },
  [MutationTypes.INCREMENT_EXCLUDED_SKILL_COUNT.VALUE]: (state: UserState) => {
    state.excludedSkillCount += 1
  },
  [MutationTypes.DECREMENT_EXCLUDED_SKILL_COUNT.VALUE]: (state: UserState) => {
    state.excludedSkillCount -= 1
  },
  [MutationTypes.SET_ACTING_AS_CLIENT.VALUE]: (
    state: UserState,
    user: any | null
  ) => {
    state.actingAsClient = user
  },
}

const actions = {
  [ActionTypes.FETCH_USER.VALUE]: async ({
    commit,
    dispatch,
  }: Store<UserState>) => {
    commit(MutationTypes.SET_FETCHING_USER_STATUS.VALUE, true)
    const { data, error } = await fetchUser()
    commit(MutationTypes.SET_FETCHING_USER_STATUS.VALUE, false)
    if (error) return null
    const user = data.data
    dispatch(ActionTypes.FETCH_PROFILE.VALUE, user.id)
    commit(MutationTypes.SET_USER.VALUE, user)

    segmentIdentify(user)

    // Important: ensure GA trackers have access to user data
    if (window.dataLayer?.push) {
      window.dataLayer.push({
        event: 'user_logged_in',
        user_id: user.id,
        email: user.email,
      })
      window.dataLayer.push({
        user_data: {
          email: user.email,
          first_name: user.firstName,
          last_name: user.lastName,
        },
      })
    }

    return user
  },
  [ActionTypes.LOG_IN.VALUE]: async (
    { commit }: Store<UserState>,
    opts: LogInDto
  ) => {
    const { data, error } = await logIn(opts.email, opts.password)
    if (error) {
      commit(metaMutations.SET_ERROR.NAMESPACED, error, { root: true })
      throw error
    }

    return data
  },
  [ActionTypes.LOG_OUT.VALUE]: async ({ commit }: Store<UserState>) => {
    const { data, error } = await logOut()

    if (error) {
      commit(metaMutations.SET_ERROR.NAMESPACED, error, { root: true })
      return { error }
    }

    commit(MutationTypes.SET_USER.VALUE, null)

    return data
  },
  [ActionTypes.REGISTER.VALUE]: async (
    { commit, dispatch }: Store<UserState>,
    opts: RegisterDto
  ) => {
    const resp = await register(opts.email, opts.password)
    if (resp.error) {
      commit(metaMutations.SET_ERROR.NAMESPACED, resp.error, { root: true })
      return resp
    }

    // make sure user is in store after register
    await dispatch(ActionTypes.FETCH_USER.VALUE)
    return resp
  },
  [ActionTypes.FORGOT_PASSWORD.VALUE]: async (
    { commit }: Store<UserState>,
    email: string
  ) => {
    const { data, error } = await forgotPassword(email)

    commit(MutationTypes.SET_FORGOT_PASSWORD_EMAIL.VALUE, email)

    if (error) {
      return null
    }

    return data
  },
  [ActionTypes.RESET_PASSWORD.VALUE]: async (
    { commit }: Store<UserState>,
    resetDto: PasswordResetDto
  ) => {
    const { data, error } = await resetPassword(resetDto)

    commit(MutationTypes.SET_FORGOT_PASSWORD_EMAIL.VALUE, null)

    if (error) {
      return null
    }

    return data
  },
  [ActionTypes.FETCH_PROFILE.VALUE]: async (
    { commit }: Store<UserState>,
    userId: string
  ) => {
    const { data, error } = await fetchProfile(userId)

    if (error) return null

    commit(MutationTypes.SET_USER_PROFILE.VALUE, data)
    return data
  },
  [ActionTypes.UPDATE_PROFILE.VALUE]: async (
    { commit, state }: Store<UserState>,
    payload: Profile
  ) => {
    const resp = await updateProfile(state.user.id, payload)

    if (resp.error) return null

    commit(MutationTypes.SET_USER_PROFILE.VALUE, resp.data)
    return resp
  },
  [ActionTypes.GET_PAYMENT_METHOD.VALUE]: async ({
    commit,
  }: Store<UserState>) => {
    const { data, error } = await getPaymentMethod()
    if (error) {
      Bugsnag.notify(error)
      return
    }

    commit(MutationTypes.SET_PAYMENT_METHOD.VALUE, data)
  },
}

const getters = {
  [GetterTypes.USER.VALUE]: (state: UserState) => {
    return state.user
  },
  [GetterTypes.FETCHING_USER.VALUE]: (state: UserState) => {
    return state.fetchingUser
  },
  [GetterTypes.CURRENT_PLAN.VALUE]: (state: UserState) => {
    return state.user?.plan
  },
  [GetterTypes.PROFILE.VALUE]: (state: UserState) => {
    return state.profile
  },
  [GetterTypes.PAYMENT_METHOD.VALUE]: (state: UserState) => {
    return state.paymentMethod
  },
  [GetterTypes.EXCLUDED_SKILL_COUNT.VALUE]: (state: UserState) => {
    return state.excludedSkillCount
  },
  [GetterTypes.USER_TYPE.VALUE]: (state: UserState) => {
    return getUserType(state.user?.plan)
  },
  [GetterTypes.IS_COACH.VALUE]: (state: UserState) => {
    // TODO: get from user data

    return true
  },
  [GetterTypes.ACTING_AS_CLIENT.VALUE]: (state: UserState) => {
    return state.actingAsClient
  },
}

function segmentIdentify(user: any) {
  try {
    /** @see https://segment.com/docs/connections/spec/identify/#traits */
    const traits = {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      plan: user.plan,
      domain: user.domain?.domain,
      createdAt: new Date(user.createdAt).toISOString(),
    }

    if (window.rudderanalytics) {
      window.rudderanalytics.identify(traits.id, traits)
    }
  } catch (ex) {
    // @ts-ignore
    Bugsnag.notify(ex)
  }
}

function getUserType(plan: string | null = null): UserType {
  if (!plan) return UserType.GUEST
  if (plan === 'free') return UserType.FREE
  if (plan === 'domain') return UserType.DOMAIN
  if (
    [
      COACH_PLANS.CAREER_COACH,
      COACH_PLANS.COACH_LITE,
      COACH_PLANS.PREMIUM_PLUS,
      COACH_PLANS.PREMIUM_PLUS_ORG,
    ].includes(plan)
  ) {
    return UserType.COACH
  }
  return UserType.PREMIUM
}

export default {
  namespaced: true,
  state: rootState,
  mutations,
  actions,
  getters,
}
